import React, { useState, useEffect, useContext, ChangeEvent, FormEvent } from 'react';
import { useGlobal } from '../context/GlobalContext';
import '../styles/ItemForm.css';
import { createProducto, updateProducto } from '../services/productos';
import { Producto, Categoria, Subcategoria, Departamento, Ubicacion } from '../types';

interface ProductFormProps {
  onClose: () => void;
  onSuccess: (message: string, newProductData?: any) => void;
  productData?: Producto;
  initialBarcode?: string;
}

const ProductForm: React.FC<ProductFormProps> = ({ onClose, onSuccess, productData, initialBarcode = '' }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio_compra: '',
    precio_venta: '',
    marca: '',
    modelo: '',
    referencia: '',
    codigo_barras: initialBarcode,
    subcategoria_id: null as number | null,
    ubicacion_id: '' as string | number, // Usamos string para el value del select, aunque sea number
  });
  const [loading, setLoading] = useState(false);
  const { data } = useGlobal();
  const ubicaciones: Ubicacion[] = Array.isArray(data?.ubicaciones) ? data.ubicaciones : [];


  const [selectedDepartamento, setSelectedDepartamento] = useState<string>('');
  const [selectedCategoria, setSelectedCategoria] = useState<string>('');

  /* Removed local fetching of ubicaciones, now using GlobalContext */

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        precio_compra: parseFloat(formData.precio_compra) || 0,
        precio_venta: parseFloat(formData.precio_venta) || 0,
        ubicacion_id: formData.ubicacion_id ? parseInt(formData.ubicacion_id.toString()) : null,
      };

      if (productData?.id) {
        const res = await updateProducto(productData.id, payload);
        const updatedProduct = (res as any)?.producto || res;
        onSuccess('¡Producto actualizado correctamente!', updatedProduct);
      } else {
        const res = await createProducto(payload);
        const newProduct = (res as any)?.producto || res;
        onSuccess('¡Producto creado correctamente!', newProduct);
      }
      setFormData({
        nombre: '',
        descripcion: '',
        precio_compra: '',
        precio_venta: '',
        marca: '',
        modelo: '',
        referencia: '',
        codigo_barras: '',
        subcategoria_id: null,
        ubicacion_id: '',
      });
    } catch (error) {
      console.error(`Error al ${productData?.id ? 'actualizar' : 'crear'} producto:`, error);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleDepartamentoChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedDepartamento(e.target.value);
    setSelectedCategoria('');
    setFormData({ ...formData, subcategoria_id: null });
  };

  const handleCategoriaChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategoria(e.target.value);
    setFormData({ ...formData, subcategoria_id: null });
  };

  // Cargar datos del producto si se proporcionan
  useEffect(() => {
    if (productData) {
      setFormData({
        nombre: productData.nombre || '',
        descripcion: productData.descripcion || '',
        precio_compra: productData.precio_compra?.toString() || '',
        precio_venta: productData.precio_venta?.toString() || '',
        marca: productData.marca || '',
        modelo: productData.modelo || '',
        referencia: productData.referencia || '',
        codigo_barras: productData.codigo_barras || '',
        subcategoria_id: productData.subcategoria_id || null,
        ubicacion_id: productData.ubicacion_id || '',
      });
      if (productData.Subcategoria) {
        const subcategoria = productData.Subcategoria;
        // @ts-expect-error: Categorium discrepancy in Sequelize model relations
        const categoria = subcategoria.Categorium || subcategoria.Categoria;
        const departamento = categoria?.Departamento;

        if (departamento) {
          setSelectedDepartamento(departamento.id.toString());
        }
        if (categoria) {
          setSelectedCategoria(categoria.id.toString());
        }
      }
    }
  }, [productData]);

  // Filtrar datos dinámicamente
  const departamentos = Array.isArray(data?.categoriesTree) ? data.categoriesTree as Departamento[] : [];
  const activeDept = departamentos.find((d) => d.id === parseInt(selectedDepartamento));
  const deptCats = (activeDept as any)?.Categorias || (activeDept as any)?.Categoria || [];
  const categorias: Categoria[] = Array.isArray(deptCats) ? deptCats : [];

  const activeCat = categorias.find((c) => c.id === parseInt(selectedCategoria));
  const catSubs = (activeCat as any)?.Subcategorias || (activeCat as any)?.Subcategoria || [];
  const subcategorias: Subcategoria[] = Array.isArray(catSubs) ? catSubs : [];


  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <h3 className="form-title">{productData?.id ? 'Editar Producto' : 'Crear Producto'}</h3>
      <label>
        Nombre:
        <input
          type="text"
          name="nombre"
          value={formData.nombre}
          onChange={handleChange}
          placeholder="Nombre del producto"
          required
        />
      </label>
      <label>
        Descripción:
        <textarea
          name="descripcion"
          value={formData.descripcion}
          onChange={handleChange}
          placeholder="Descripción del producto"
        />
      </label>
      <label>
        Precio de Compra (Solo numeros):
        <input
          type="text" inputMode="numeric" pattern="\d*"
          name="precio_compra"
          value={formData.precio_compra}
          onChange={handleChange}
          placeholder="Precio de compra"
          required
        />
      </label>
      <label>
        Precio de Venta (Solo numeros):
        <input
          type="text" inputMode="numeric" pattern="\d*"
          name="precio_venta"
          value={formData.precio_venta}
          onChange={handleChange}
          placeholder="Precio de venta"
          required
        />
      </label>
      <label>
        Marca:
        <input
          type="text"
          name="marca"
          value={formData.marca}
          onChange={handleChange}
          placeholder="Marca del producto"
        />
      </label>
      <label>
        Modelo:
        <input
          type="text"
          name="modelo"
          value={formData.modelo}
          onChange={handleChange}
          placeholder="Modelo del producto"
        />
      </label>
      <label>
        Referencia:
        <input
          type="text"
          name="referencia"
          value={formData.referencia}
          onChange={handleChange}
          placeholder="Referencia del producto"
        />
      </label>
      <label>
        Departamento:
        <select value={selectedDepartamento} onChange={handleDepartamentoChange}>
          <option value="">Seleccione un departamento</option>
          {departamentos.map((departamento) => (
            <option key={departamento.id} value={departamento.id}>
              {departamento.nombre}
            </option>
          ))}
        </select>
      </label>

      {(categorias.length > 0 || selectedCategoria) && (
        <label>
          Categoría:
          <select value={selectedCategoria} onChange={handleCategoriaChange}>
            <option value="">Seleccione una categoría</option>
            {categorias.map((categoria) => (
              <option key={categoria.id} value={categoria.id}>
                {categoria.nombre}
              </option>
            ))}
          </select>
        </label>
      )}

      {(subcategorias.length > 0 || formData.subcategoria_id) && (
        <label>
          Subcategoría:
          <select
            name="subcategoria_id"
            value={formData.subcategoria_id || ''}
            onChange={handleChange}
          >
            <option value="">Seleccione una subcategoría</option>
            {subcategorias.map((subcategoria) => (
              <option key={subcategoria.id} value={subcategoria.id}>
                {subcategoria.nombre}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Ubicación:
        <select
          name="ubicacion_id"
          value={formData.ubicacion_id || ''}
          onChange={handleChange}
        >
          <option value="">Seleccione una ubicación</option>
          {ubicaciones.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nombre}
            </option>
          ))}
        </select>
      </label>
      <label>
        Código de Barras (EAN):
        <input
          type="text"
          name="codigo_barras"
          value={formData.codigo_barras}
          onChange={handleChange}
          placeholder="Escanee o ingrese código"
        />
      </label>
      <button type="submit" className="button primary" disabled={loading}>
        {loading ? 'Guardando...' : productData?.id ? 'Actualizar' : 'Crear'}
      </button>
      <button
        type="button"
        className="button secondary"
        onClick={onClose}
        disabled={loading}
      >
        Cancelar
      </button>
    </form>
  );
};

export default ProductForm;